import React, { PropsWithChildren, useEffect, useState } from 'react';
import Panel from '../Panel';
import CopySection, { FinishStepButton } from '../CopySection';
import styles from './WorkspacePanel.module.scss';
import analytics from '@repo/utils/helpers/analytics';
import WorkspaceAvatar from './WorkspaceAvatar';
import { useWorkspaceContext } from '@repo/hooks';
import { apiRequest } from '@repo/utils/helpers/api';
import { WorkspaceAPIItem } from '@repo/utils/models/Workspace';

const FormField = ({
  label,
  children,
  optional,
}: PropsWithChildren<{ label: string; optional?: boolean }>) => (
  <div>
    <label className="form-label">
      {label} {optional && <span className="optional">(optional)</span>}
    </label>
    {children}
  </div>
);

export default function WorkspacePanel({ proceedToNextStep }) {
  const { activeWorkspace, updateActiveWorkspace } = useWorkspaceContext();
  const [savingWorkspace, setSavingWorkspace] = useState(false);
  const [imageError, setImageError] = useState<string>(undefined);
  const [savingFailed, setSavingFailed] = useState(false);
  const [avatarImgFileToUpload, setAvatarImgFileToUpload] = useState<null | File>(null);

  const approveWorkspaceAndGoToNextStep = async () => {
    setSavingWorkspace(true);
    const originalAvatarURL = activeWorkspace?.avatarURL;
    const formData = new FormData();
    formData.append('name', activeWorkspace.name);
    if (avatarImgFileToUpload) {
      formData.append('avatar_url', avatarImgFileToUpload);
    }

    apiRequest<WorkspaceAPIItem>('PATCH', '/workspaces/active/', formData, { apiVersion: 'v2' })
      .then((workspace) => {
        updateActiveWorkspace({ avatarURL: workspace.avatar_url });
        proceedToNextStep();
      })
      .catch((err) => {
        console.error(err);
        updateActiveWorkspace({ avatarURL: originalAvatarURL });
        setSavingFailed(true);
      })
      .finally(() => {
        setSavingWorkspace(false);
      });
  };

  useEffect(() => {
    analytics.track('Onboarding in Progress', { screen: 'Profile' });
  }, []);

  useEffect(() => {
    if (savingFailed) {
      setSavingFailed(false);
    }
  }, [activeWorkspace]);

  let errorMessage = undefined;
  if (imageError) {
    errorMessage = imageError;
  } else if (savingFailed) {
    errorMessage = 'Something went wrong, please try again';
  }

  return (
    <Panel>
      <CopySection>
        <h2>Create a team workspace</h2>
        <p>Fill in your workspace details for you and your teammates.</p>
        <FinishStepButton
          onClick={approveWorkspaceAndGoToNextStep}
          disabled={savingWorkspace || !activeWorkspace?.name}
          disabledMsg="Enter a workspace name to continue"
          loading={savingWorkspace}
          errorMsg={errorMessage}
        >
          Continue
        </FinishStepButton>
      </CopySection>
      <div className={styles.workspaceForm}>
        <WorkspaceAvatar
          setAvatarImgFileToUpload={setAvatarImgFileToUpload}
          setImageError={setImageError}
        />
        <FormField label="Workspace Name">
          <input
            className="form-input"
            type="text"
            value={activeWorkspace?.name || ''}
            onChange={(e) => updateActiveWorkspace({ name: e.target.value })}
          />
        </FormField>
      </div>
    </Panel>
  );
}
